import React, { useState, useEffect } from "react";
import { useAuthenticated, useNotify } from "react-admin";
import axiosWithAuth from "../../utils/axiosWithAuth";

import {
  makeStyles,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  CircularProgress,
  Box,
  Chip,
} from "@material-ui/core";
import Alert from "@mui/material/Alert";
import RefreshIcon from "@material-ui/icons/Refresh";
import AddIcon from "@material-ui/icons/Add";

const API_BASE = `/admin/predictor-import`;

async function apiGet(path) {
  const res = await axiosWithAuth.get(`${API_BASE}${path}`);
  return res.data;
}

async function apiPost(path, data) {
  const res = await axiosWithAuth.post(`${API_BASE}${path}`, data);
  return res.data;
}

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  paper: { padding: theme.spacing(3), marginTop: theme.spacing(2) },
  yearInput: { width: 120, marginRight: theme.spacing(1) },
  chip: { margin: theme.spacing(0.25) },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
}));

const PredictorYearColumns = () => {
  useAuthenticated();
  const classes = useStyles();
  const notify = useNotify();

  const [predictors, setPredictors] = useState([]);
  const [yearData, setYearData] = useState({}); // { [key]: { cols: [], loading, error } }
  const [newYear, setNewYear] = useState({}); // { [key]: string }
  const [adding, setAdding] = useState({}); // { [key]: bool }

  const fetchYearColumns = async (key) => {
    setYearData((prev) => ({
      ...prev,
      [key]: { ...prev[key], loading: true, error: null },
    }));
    try {
      const d = await apiGet(`/year-columns/${key}`);
      setYearData((prev) => ({
        ...prev,
        [key]: { cols: d.yearColumns || [], loading: false, error: null },
      }));
    } catch (e) {
      setYearData((prev) => ({
        ...prev,
        [key]: {
          cols: [],
          loading: false,
          error: e.response?.data?.error || "Failed to load",
        },
      }));
    }
  };

  useEffect(() => {
    apiGet("/predictors")
      .then((d) => {
        const widePredictors = (d.data || []).filter(
          (p) => p.tableType === "wide",
        );
        setPredictors(widePredictors);
        widePredictors.forEach((p) => fetchYearColumns(p.key));
      })
      .catch(() => notify("Failed to load predictor list", "error"));
  }, [notify]);

  const handleAddYear = async (key) => {
    const year = (newYear[key] || "").trim();
    if (!/^\d{4}$/.test(year)) {
      notify("Please enter a valid 4-digit year", "warning");
      return;
    }
    setAdding((prev) => ({ ...prev, [key]: true }));
    try {
      const result = await apiPost("/year-column", { predictorKey: key, year });
      if (result.alreadyExists) {
        notify(`Column ${result.columnName} already exists`, "info");
      } else {
        notify(
          `Added column ${result.columnName} to ${result.tableName}`,
          "success",
        );
        // Refresh the column list for this predictor
        fetchYearColumns(key);
      }
    } catch (e) {
      notify(e.response?.data?.error || "Failed to add column", "error");
    } finally {
      setAdding((prev) => ({ ...prev, [key]: false }));
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.headerRow}>
        <Typography variant="h5">Predictor Year Columns</Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => predictors.forEach((p) => fetchYearColumns(p.key))}
          variant="outlined"
          size="small"
        >
          Refresh All
        </Button>
      </div>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        Manage the yearly closing-rank columns on wide-table predictors. Add a
        new year column before importing data that contains it.
      </Typography>

      <TableContainer
        component={Paper}
        className={classes.paper}
        variant="outlined"
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Predictor</strong>
              </TableCell>
              <TableCell>
                <strong>Existing Year Columns</strong>
              </TableCell>
              <TableCell>
                <strong>Add New Year</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {predictors.map((p) => {
              const state = yearData[p.key] || {
                cols: [],
                loading: true,
                error: null,
              };
              return (
                <TableRow key={p.key}>
                  <TableCell>
                    <Typography variant="body2">
                      <strong>{p.label}</strong>
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {p.key}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {state.loading && <CircularProgress size={18} />}
                    {state.error && (
                      <Alert
                        severity="error"
                        style={{ padding: "2px 8px", fontSize: 12 }}
                      >
                        {state.error}
                      </Alert>
                    )}
                    {!state.loading &&
                      !state.error &&
                      (state.cols.length > 0 ? (
                        state.cols.map((col) => (
                          <Chip
                            key={col}
                            label={col}
                            size="small"
                            className={classes.chip}
                          />
                        ))
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          No year columns found
                        </Typography>
                      ))}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <TextField
                        className={classes.yearInput}
                        label="Year"
                        variant="outlined"
                        size="small"
                        placeholder="2026"
                        value={newYear[p.key] || ""}
                        onChange={(e) =>
                          setNewYear((prev) => ({
                            ...prev,
                            [p.key]: e.target.value,
                          }))
                        }
                        inputProps={{ maxLength: 4 }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={
                          adding[p.key] ? (
                            <CircularProgress size={14} />
                          ) : (
                            <AddIcon />
                          )
                        }
                        disabled={adding[p.key] || !newYear[p.key]}
                        onClick={() => handleAddYear(p.key)}
                      >
                        Add
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {predictors.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default PredictorYearColumns;
